import 'braid-design-system/reset';
import { ApolloProvider } from '@apollo/client';
import type { EmployerSiteName } from '@seek/adv-header-footer';
import { useIsNVLEnabled } from '@seek/uiv-banner';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import apac from 'braid-design-system/themes/apac';
import seekJobs from 'braid-design-system/themes/seekJobs';
import setDebug from 'debug';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import TrackingLink from 'src/components/common/TrackingLink/TrackingLink';
import { checkSessionExist } from 'src/modules/authHandler';
import getSsoClient from 'src/modules/authHandler/utils/getSsoClient';
import {
  initializeFullStory,
  initializeIntercom,
} from 'src/modules/experienceMonitor';
import {
  convertToAccountInfo,
  getHeaderFooterContext,
} from 'src/modules/headerFooterContext';
import { hirerGqlClient } from 'src/services/apollo/client/hirer-graph-client';
import type { AccountInfo, Environment, LocaleWithoutZhHK } from 'src/types';
import { getSeekId } from 'src/utils/account-selection';

import { getVocabLanguage } from '../utils/getVocabLanguage';

import ContentPage from './ContentPage';
import ErrorPage from './ErrorPage';
import MainLayout from './MainLayout';
import type { HeaderFooterContext } from './generated/graphql-types';

const debug = setDebug('App');

interface Props {
  locale: LocaleWithoutZhHK;
  site: EmployerSiteName;
  environment: Environment;
}

export default function App({ locale, site, environment }: Props) {
  const [appState, setAppState] = useState('LOADING');
  const [headerFooterContext, setHeaderFooterContext] = useState<
    HeaderFooterContext | undefined
  >(undefined);
  const path = useLocation().pathname;

  useEffect(() => {
    (async () => {
      debug('Checking auth state...');

      await checkSessionExist();

      debug('Selecting advertiser...');
      try {
        const seekId = getSeekId();

        if (!seekId) {
          throw new Error(
            `Seek id ${seekId} was not found in local storage! Redirecting to sign in`,
          );
        }
      } catch (err) {
        debug(err);
        getSsoClient().login();
      }

      debug('Fetching account info...');

      try {
        const headerFooterContextResp = await getHeaderFooterContext();

        if (headerFooterContextResp) {
          const accountInfo: AccountInfo = convertToAccountInfo(
            headerFooterContextResp,
          );
          initializeFullStory(accountInfo);
          initializeIntercom(accountInfo);
        }

        setAppState('READY');
        setHeaderFooterContext(headerFooterContextResp);
      } catch (err) {
        setAppState('ERROR');

        if (err instanceof Error) {
          throw new Error(err.message);
        }
      }
    })();
  }, [path]);

  const content = appState === 'ERROR' ? <ErrorPage /> : <ContentPage />;
  const isNVLEnabled = useIsNVLEnabled(false);
  return (
    <VocabProvider language={getVocabLanguage(locale)}>
      <BraidProvider
        theme={isNVLEnabled ? seekJobs : apac}
        linkComponent={TrackingLink}
      >
        <ToastProvider>
          <ApolloProvider client={hirerGqlClient}>
            <MainLayout
              site={site}
              locale={locale}
              environment={environment}
              headerFooterContext={headerFooterContext}
            >
              {content}
            </MainLayout>
          </ApolloProvider>
        </ToastProvider>
      </BraidProvider>
    </VocabProvider>
  );
}
